import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import useLoan from 'hooks/useLoan';
import Routes from 'resources/routes.json';

import { getFirstName } from 'utils/helper/loan-helper';
import { isMobile } from '../../../../../utils/helper/page-behavior-helper';
import { getCounterOffer, FIND_COUNTER_OFFER_SUCCESS } from '../../../../../store/actions/application-actions';
import { findCurrentLoan } from '../../../../../store/actions/customer-actions';
import { goToLoanRoute } from '../../../../../navigation/application-routes';

import Loading from 'components/features/loading/loading';
import { Button, Container, Typography } from 'lib-ui-react';

import CounterOfferLoanAmount from './loan-amount';
import CounterOfferInstallmentValue from './installment-value';
import CounterOfferInstallmentsQuantity from './number-installments';
import CounterOfferInstallmentDueDate from './installment-due-date';
import useEvents from '../../../../../hooks/useEvents';

function CounterOfferProcess() {
  const dispatch = useDispatch();
  const { dispatchCompleteEvent } = useEvents('application-customer-portal-web-page-contract-recurring-counter-offer', 'recurring-counter-offer')
  const { loan, device, findLoanAndRedirect } = useLoan();

  const [loading, setLoading] = useState(false);
  const [loanAmount, setLoanAmount] = useState(false);
  const [numberOfInstallments, setNumberOfInstallments] = useState(false);
  const [installmentValue, setInstallmentValue] = useState(false);
  const [installmentDueDate, setInstallmentDueDate] = useState(false);

  const buttonMaxWidth = isMobile() ? { maxWidth: 'none' } : { minWidth: 'none' };

  function handleLoanAmount() {
    dispatchCompleteEvent('loan-amount')
    setLoanAmount(!loanAmount);
  }

  function handleHighInterestRate() {
    dispatchCompleteEvent('high-interest-rate')
    setLoading(true);
    dispatch(getCounterOffer(device, 'high-interest-rate', 'high', loan?.id)).then((response) => {
      if (response.type === FIND_COUNTER_OFFER_SUCCESS) {
        dispatch(findCurrentLoan(device)).then(({ payload: loan }) => {
          setLoading(false);
          sessionStorage.setItem('startProcessCounterOfferRecurrent', false);
          goToLoanRoute(loan, Routes.LOAN_COUNTER_OFFER_PROCESS, Routes.LOAN_APPLICATION_CONTRACT);
        });
      }
    });
  }

  function handleInstallmentValue() {
    dispatchCompleteEvent('loan-installment-value')
    setInstallmentValue(!installmentValue);
  }

  function handleNumberOfInstallments() {
    dispatchCompleteEvent('loan-installments-quantity')
    setNumberOfInstallments(!numberOfInstallments);
  }

  function handleInstallmentDueDate() {
    dispatchCompleteEvent('loan-first-installment-due-date')
    setInstallmentDueDate(!installmentDueDate);
  }

  function handleReceivedBetterOffer() {
    dispatchCompleteEvent('received-better-offer')
    setLoading(true);
    dispatch(getCounterOffer(device, 'received-better-offer', 'low', loan?.id)).then((response) => {
      if (response.type === FIND_COUNTER_OFFER_SUCCESS) {
        dispatch(findCurrentLoan(device)).then(({ payload: loan }) => {
          setLoading(false);
          sessionStorage.setItem('startProcessCounterOfferRecurrent', false);
          sessionStorage.setItem('betterConditionsFinded', response.payload ? 'true' : 'false');

          goToLoanRoute(loan, Routes.LOAN_COUNTER_OFFER_PROCESS, Routes.LOAN_APPLICATION_CONTRACT);
        });
      }
    });
  }

  function handleIDontNeed() {
    dispatchCompleteEvent('not-needed')
    setLoading(true);
    dispatch(getCounterOffer(device, 'not-needed', 'low', loan?.id)).then((response) => {
      if (response.type === FIND_COUNTER_OFFER_SUCCESS) {
        dispatch(findCurrentLoan(device)).then(({ payload: loan }) => {
          setLoading(false);
          sessionStorage.setItem('startProcessCounterOfferRecurrent', false);
          sessionStorage.setItem('betterConditionsFinded', response.payload ? 'true' : 'false');

          goToLoanRoute(loan, Routes.LOAN_COUNTER_OFFER_PROCESS, Routes.LOAN_APPLICATION_CONTRACT);
        });
      }
    });
  }


  useEffect(() => {
    findLoanAndRedirect(Routes.LOAN_COUNTER_OFFER_PROCESS, Routes.LOAN_COUNTER_OFFER_PROCESS)
  }, [device])

  if (loading) {
    return (
      <>
        <Loading title="Aguarde um momento, por favor..." />
      </>
    );
  }

  return (
    <Container align="center">
      <Container maxWidth="480px">
        {loanAmount && <CounterOfferLoanAmount />}
        {installmentValue && <CounterOfferInstallmentValue />}
        {numberOfInstallments && <CounterOfferInstallmentsQuantity />}
        {installmentDueDate && <CounterOfferInstallmentDueDate />}
        {!loanAmount && !numberOfInstallments && !installmentValue && !installmentDueDate && (
          <Container spacing={24} padding={16} height="100vh">
            <Container spacing={8}>
              <Typography type="title" size="medium" fontWeight={400} lineHeight="28px" color="#2A2B35">
                <b>{getFirstName(loan)}, conta pra gente:</b> Como podemos melhorar o seu empréstimo?
              </Typography>

              <Typography size="small" fontWeight={400} lineHeight="20px" color="#757575">
                Selecione uma opção abaixo.
              </Typography>
            </Container>

            <Container align="center" justify="center" spacing={8}>
              <Button
                id="loanAmount"
                outlined
                color="#757575"
                borderColor="#E0E0E0"
                margin="0"
                style={buttonMaxWidth}
                minWidth="320px"
                onClick={handleLoanAmount}
              >
                Valor do empréstimo
              </Button>
              <Button
                id="highInterestRate"
                outlined
                color="#757575"
                borderColor="#E0E0E0"
                margin="0"
                style={buttonMaxWidth}
                minWidth="320px"
                onClick={handleHighInterestRate}
              >
                Juros altos
              </Button>
              <Button
                id="installmentValue"
                outlined
                color="#757575"
                borderColor="#E0E0E0"
                margin="0"
                style={buttonMaxWidth}
                minWidth="320px"
                onClick={handleInstallmentValue}
              >
                Valor da parcela
              </Button>
              <Button
                id="installmentsQuantity"
                outlined
                color="#757575"
                borderColor="#E0E0E0"
                margin="0"
                style={buttonMaxWidth}
                minWidth="320px"
                onClick={handleNumberOfInstallments}
              >
                Quantidade de parcelas
              </Button>
              <Button
                id="firstInstallmentDueDate"
                outlined
                color="#757575"
                borderColor="#E0E0E0"
                margin="0"
                style={buttonMaxWidth}
                minWidth="320px"
                onClick={handleInstallmentDueDate}
              >
                Data de vencimento da parcela
              </Button>
              <Button
                id="receivedBetterOffer"
                outlined
                color="#757575"
                borderColor="#E0E0E0"
                margin="0"
                style={buttonMaxWidth}
                minWidth="320px"
                onClick={handleReceivedBetterOffer}
              >
                Recebi uma oferta melhor
              </Button>
              <Button
                id="notNeeded"
                outlined
                color="#757575"
                borderColor="#E0E0E0"
                margin="0"
                style={buttonMaxWidth}
                minWidth="320px"
                onClick={handleIDontNeed}
              >
                Não preciso
              </Button>
            </Container>
          </Container>
        )}
      </Container>
    </Container>
  );
}

export default CounterOfferProcess;
